import { useTranslation } from "react-i18next";
import { IS_MOBILE } from "@/lib/environmentDetector";
import FileAttachmentPreview from "../../FileAttachmentPreview";
import MediaAttachmentPreview from "../../MediaAttachmentPreview";
import { ChangeEvent, RefObject, useEffect } from "react";

interface RtfInputMainProps {
  textareaRef: RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  charCount: number;
  charLimit: number;
  approxRemainingChars: number;
  showCloseToLimitWarning: boolean;
  isOverCharLimit: boolean;
  isOverEncryptedLimit: boolean;
  actionButtons?: React.ReactNode;
  mobileBubble?: React.ReactNode;
}

export default function RtfInputMain({
  textareaRef,
  value,
  onChange,
  onKeyDown,
  onPaste,
  charCount,
  charLimit,
  approxRemainingChars,
  showCloseToLimitWarning,
  isOverCharLimit,
  isOverEncryptedLimit,
  actionButtons,
  mobileBubble,
}: RtfInputMainProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    // Ensure textarea auto-resizes when value is updated programmatically
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [value, textareaRef]);

  const showCounter =
    showCloseToLimitWarning || isOverCharLimit || isOverEncryptedLimit;

  const isOverLimit = isOverCharLimit || isOverEncryptedLimit;

  return (
    <main className="relative flex flex-1 flex-col rounded-xl border border-border p-2">
      {mobileBubble}
      <section className="flex-1">
        <div className="custom-scroll custom-scroll-x max-h-40 overflow-y-auto">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            placeholder={t("editor.writePlaceholder")}
            aria-label={t("editor.writePlaceholder")}
            autoCapitalize={IS_MOBILE ? "sentences" : "off"}
            className="min-h-8 w-full flex-1 resize-none border-none bg-transparent px-0.5 py-1 text-base leading-tight text-foreground placeholder:text-muted-foreground/50 focus-visible:border-none focus-visible:outline-none focus-visible:ring-0"
            rows={1}
            onInput={(e) => {
              // Auto-resize textarea
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "auto";
              target.style.height = `${target.scrollHeight}px`;
            }}
          />
        </div>
        <FileAttachmentPreview />
        <MediaAttachmentPreview />
        <div aria-live="polite">
          {showCounter &&
            (isOverLimit ? (
              <div className="mt-2 flex items-center justify-between rounded-md border border-destructive/60 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                <span>{t("editor.messageTooLarge")}</span>
                <span className="font-mono ml-3 text-[11px]">
                  {charCount.toLocaleString()}
                  {"/"}
                  {charLimit.toLocaleString()}
                </span>
              </div>
            ) : (
              <div className="mt-1 flex justify-end text-xs text-muted-foreground">
                <span>
                  ~{approxRemainingChars.toLocaleString()}{" "}
                  {t("editor.charactersRemaining", {
                    defaultValue: "characters remaining",
                  })}{" "}
                  ({charCount.toLocaleString()}
                  {"/"}
                  {charLimit.toLocaleString()})
                </span>
              </div>
            ))}
        </div>
      </section>
      {actionButtons && (
        <div className="flex items-center gap-3">{actionButtons}</div>
      )}
    </main>
  );
}
